import type { ReactNode } from "react";
import FocusTrap from "focus-trap-react";
import { animated, type SpringValues } from "@react-spring/web";

import { HaNDetailsContext, type HaNSelection, HaNViewStoreContext, type HereAndNowTab } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";

import { useConfirmDiscardingBooking } from "../../../details/useConfirmDiscardingBooking";

type HaNDetailsPanelProps = {
  style: SpringValues<{ transform: string; opacity: number }>;
  tab: HereAndNowTab;
  selection: HaNSelection;
  isOpen: boolean;
  children: ReactNode;
};

/**
 * Keeps keyboard focus in the panel while it's open, and Esc closes it. A click outside lets go of the focus, and
 * the trap is keyed by the selection, so opening something else catches the focus again. While it slides closed
 * it's inert.
 */
export const HaNDetailsPanel = ({ style, tab, selection, isOpen, children }: HaNDetailsPanelProps) => {
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const confirmDiscardingBooking = useConfirmDiscardingBooking();

  const close = () => {
    confirmDiscardingBooking(viewStore.closeDetails);
  };

  return (
    <HaNDetailsContext.Provider value={{ tab, selection, close }}>
      <FocusTrap
        key={selection.id}
        active={isOpen}
        focusTrapOptions={{
          allowOutsideClick: true,
          clickOutsideDeactivates: true,
          escapeDeactivates: () => {
            close();

            return false;
          },
          // For details with nothing to tab to, the trap needs somewhere to put the focus
          fallbackFocus: "[data-han-details]",
          preventScroll: true,
        }}>
        <animated.section
          data-han-details=""
          aria-hidden={!isOpen || undefined}
          inert={!isOpen}
          tabIndex={-1}
          style={style}
          className="pointer-events-auto flex h-full w-full flex-col overflow-hidden border-r bg-white [grid-area:1/1] focus:outline-none max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-[30px] max-sm:h-auto max-sm:rounded-t-lg max-sm:border-r-0 lg:mb-[18px] lg:ml-5 lg:mt-[70px] lg:h-auto lg:w-[400px] lg:rounded-lg lg:border-r-0 lg:shadow-mapiq-base">
          {children}
        </animated.section>
      </FocusTrap>
    </HaNDetailsContext.Provider>
  );
};
